import { useMemo } from "react";
import { Instances, Instance } from "@react-three/drei";
import * as THREE from "three";
import { tokens } from "../../styles/tokens";
import type { SensorInstall } from "../../stores/layoutStore";
import type { SensorReading } from "../../stores/telemetryStore";
import { useTelemetryStore } from "../../stores/telemetryStore";

const BEACON_RADIUS = 0.15;

const HEALTH_COLOR: Record<SensorReading["health"], string> = {
  ok:      tokens.semantic.success,
  warn:    tokens.semantic.warn,
  fail:    tokens.semantic.danger,
  offline: tokens.ink[500],
};

interface InstanceProps {
  sensor: SensorInstall;
  reading: SensorReading | undefined;
  onSelect: (id: string) => void;
}

function SensorInstance({ sensor, reading, onSelect }: InstanceProps) {
  const health = reading?.health ?? "offline";
  const color = useMemo(
    () => new THREE.Color(HEALTH_COLOR[health]),
    [health],
  );
  return (
    <Instance
      position={[sensor.pos.x, sensor.pos.y, sensor.pos.z]}
      color={color}
      onClick={(e) => { e.stopPropagation(); onSelect(sensor.id); }}
    />
  );
}

interface Props {
  sensors: SensorInstall[];
}

export function SensorBeacon({ sensors }: Props) {
  const sensorReadings = useTelemetryStore((s) => s.sensorReadings);
  const select         = useTelemetryStore((s) => s.select);

  const geo = useMemo(() => new THREE.IcosahedronGeometry(BEACON_RADIUS, 0), []);
  const mat = useMemo(
    () => new THREE.MeshStandardMaterial({ roughness: 0.4, metalness: 0.3, vertexColors: true, toneMapped: false }),
    [],
  );

  return (
    <Instances geometry={geo} material={mat} limit={256}>
      {sensors.map((s) => (
        <SensorInstance
          key={s.id}
          sensor={s}
          reading={sensorReadings[s.id]}
          onSelect={select}
        />
      ))}
    </Instances>
  );
}
